import React, { type MouseEventHandler, type ReactNode, type Ref, forwardRef } from 'react';
import { Link, LinkProps } from 'react-router-dom';

import { LoadingButton } from '@mui/lab';
import { CircularProgress, ButtonProps as MaterialButtonProps } from '@mui/material';
import { VISUAL_REFRESH } from 'common/utils';
import { Button as DsButton, ButtonLink as DsButtonLink } from 'components';

import { toDsProps } from './toDsProps';
import { mapButtonSizeToLoaderSize } from './utils/mapButtonSizeToLoaderSize';

export type ButtonProps = Omit<
  Pick<
    MaterialButtonProps,
    | 'children'
    | 'disabled'
    | 'fullWidth'
    | 'href'
    | 'size'
    | 'startIcon'
    | 'endIcon'
    | 'sx'
    | 'type'
    | 'variant'
    | 'tabIndex'
    | 'onClick'
    | 'ref'
    | 'form'
    | 'disableRipple'
  >,
  'ref'
> & {
  color?: 'primary' | 'secondary' | 'info' | 'error';
  externalHref?: string;
  isLoading?: boolean;
  loadingIndicator?: ReactNode;
};

// This is necessary because otherwise we get a console error from MUI as it uses ref
const LinkComponent = React.forwardRef<HTMLAnchorElement, LinkProps>(({ to, ...props }, ref) => (
  <Link innerRef={ref} to={to} {...props} />
));

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      externalHref,
      href,
      isLoading = false,
      loadingIndicator,
      size = 'medium',
      variant = 'contained',
      ...props
    },
    ref
  ) => {
    /**
     * The visual-refresh gate lives HERE, not at the call sites — all 244 of them keep this
     * component's MUI-shaped API and never mention the flag. `toDsProps` returns `null` for anything
     * DS 2.0 cannot express, which falls through to the MUI implementation below.
     *
     * Links keep their element. MUI already renders an anchor for `href` (via `LinkComponent` + `to`)
     * and for `externalHref`, so swapping in `ButtonLink` changes the class list and nothing else —
     * same react-router navigation, same cmd-click and middle-click, same `link` role.
     */
    const dsProps = VISUAL_REFRESH
      ? toDsProps({ children, externalHref, href, isLoading, loadingIndicator, size, variant, ...props })
      : null;

    if (dsProps) {
      // Consumed or refused by `toDsProps`, and meaningless on a component with no Emotion and no
      // ripple — so they must not be forwarded. Everything else (`data-testid`, `onClick`,
      // `fullWidth`, icons, `tabIndex`) passes straight through.
      const { color: _color, disableRipple: _disableRipple, sx: _sx, ...rest } = props;

      if (href || externalHref) {
        // An anchor accepts none of these. `toDsProps` refuses a disabled link outright, so dropping
        // `disabled` here cannot lose behaviour.
        const { disabled: _disabled, form: _form, onClick, type: _type, ...linkProps } = rest;

        // `ButtonProps` inherits MUI's button-shaped handler, but the element under these branches
        // is — and already was, under MUI — an anchor. The cast corrects the element type rather
        // than changing any behaviour; `MouseEventHandler` is invariant in it, so it cannot be
        // widened away.
        const handleClick = onClick as MouseEventHandler<HTMLAnchorElement> | undefined;

        // Same correction, same reason: `forwardRef<HTMLButtonElement>` describes the common case,
        // but these two branches render an anchor. The legacy path below forwards the ref to that
        // anchor (via `loadingButtonProps`), so omitting it here would make the ref arrive under
        // one flag state and vanish under the other — silently, with no type error, since `sx` is
        // not the only prop a Tailwind component can swallow. `Ref` is invariant in its element, so
        // it needs the double cast.
        const anchorRef = ref as unknown as Ref<HTMLAnchorElement>;

        // `ButtonLink` has no busy state, so `loading` is not one of its props and spreading it puts
        // a literal `loading="false"` on the anchor — an invalid DOM attribute React warns about.
        // Always exactly `false` here rather than sometimes `true`, because `toDsProps` already
        // refuses a busy link outright, so nothing is lost by dropping it.
        const { loading: _loading, ...dsLinkProps } = dsProps;

        return href ? (
          <DsButtonLink {...linkProps} {...dsLinkProps} ref={anchorRef} onClick={handleClick} render=<Link to={href} />>
            {children}
          </DsButtonLink>
        ) : (
          <DsButtonLink
            {...linkProps}
            {...dsLinkProps}
            ref={anchorRef}
            href={externalHref}
            onClick={handleClick}
            rel="noopener noreferrer"
            target="_blank"
          >
            {children}
          </DsButtonLink>
        );
      }

      return (
        <DsButton ref={ref} {...rest} {...dsProps}>
          {children}
        </DsButton>
      );
    }

    const loadingButtonProps = {
      ref,
      href: externalHref,
      loadingIndicator: loadingIndicator || <CircularProgress color="inherit" size={mapButtonSizeToLoaderSize(size)} />,
      LinkComponent: href ? LinkComponent : undefined,
      loading: isLoading,
      size,
      target: externalHref ? '_blank' : undefined,
      to: href,
      variant,
      ...props,
    };

    return (
      <LoadingButton {...loadingButtonProps}>
        {/**
         * This is required because MUI renders the children even when loading
         * and this way the width of the button does not match the designs
         */}
        {!isLoading ? children : null}
      </LoadingButton>
    );
  }
);
